import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { request } from '@/lib/api.js';
import {
  EmptyState,
  GlassButton,
  LoadingState,
  PageHeader,
} from '@/components/ui/index.js';
import { RadarChart, type RadarDimension } from '@/components/charts/index.js';
import { useFmt } from '@/lib/useFmt.js';
import type {
  VisitorAssessment,
  VisitorAttempt,
  VisitorDetailResponse,
} from '@/lib/types.js';

/**
 * Drill-in for a single visitor — every question they answered, every
 * completed assessment (with radar + composite), full conversion path.
 *
 * Most of the heavy lifting is just decoding the JSON columns
 * (`dimension_scores`, `question_ids`, `picked_frequencies`) the API
 * passes through as strings. Defensive parse on each so a single bad row
 * doesn't blow up the whole page.
 */
export function VisitorDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<VisitorDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fmtDateTime } = useFmt();

  useEffect(() => {
    if (id === undefined) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await request<VisitorDetailResponse>(`/admin/visitors/${id}`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '加载失败');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading && data === null) return <LoadingState />;
  if (error !== null) {
    return (
      <div className="rounded-lg bg-rose-900/30 border border-rose-700/50 px-3 py-2 text-sm text-rose-300">
        {error}
      </div>
    );
  }
  if (data === null) return <EmptyState title="未找到该访客" icon="🤷" />;

  const { visitor, attempts, assessments } = data;
  const converted = visitor.converted_user_id !== null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="访客详情"
        subtitle={`visitor_id · ${visitor.id}`}
        right={
          <GlassButton size="sm" variant="ghost">
            <Link to="/visitors">← 返回列表</Link>
          </GlassButton>
        }
      />

      {/* Metadata + conversion banner */}
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5 space-y-3 backdrop-blur">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <Field label="国家">{visitor.country ?? '—'}</Field>
          <Field label="语言">{visitor.locale ?? '—'}</Field>
          <Field label="首次访问">{fmtDateTime(visitor.first_seen_at)}</Field>
          <Field label="最近访问">{fmtDateTime(visitor.last_seen_at)}</Field>
        </div>
        {visitor.referrer !== null && (
          <Field label="来源">
            <span className="text-slate-400 break-all">{visitor.referrer}</span>
          </Field>
        )}
        {visitor.user_agent !== null && (
          <Field label="User Agent">
            <span className="text-slate-500 text-xs break-all">{visitor.user_agent}</span>
          </Field>
        )}
      </div>

      {converted && visitor.converted_user_id !== null && (
        <div className="rounded-xl border border-emerald-700/50 bg-emerald-900/15 p-5 backdrop-blur">
          <p className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-1">
            ✓ 已转化为注册用户
          </p>
          <p className="text-sm text-slate-300">
            转化时间 · {fmtDateTime(visitor.converted_at)}
          </p>
          <div className="mt-3">
            <Link
              to={`/users/${visitor.converted_user_id}`}
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-600/30 text-sm transition-colors"
            >
              {visitor.converted_user_email ?? visitor.converted_user_id.slice(0, 8)} →
            </Link>
          </div>
        </div>
      )}

      {/* Assessments */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-shark-300 mb-3">
          能力测评 · {assessments.length} 次
        </h2>
        {assessments.length === 0 ? (
          <p className="text-sm text-slate-500 italic">还没有完成的测评。</p>
        ) : (
          <div className="space-y-4">
            {assessments.map((a) => (
              <AssessmentCard key={a.id} a={a} fmtDateTime={fmtDateTime} />
            ))}
          </div>
        )}
      </section>

      {/* Attempts */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-widest text-shark-300 mb-3">
          答题记录 · {attempts.length} 道
          {attempts.length >= 500 && (
            <span className="text-slate-500 ml-2 font-normal">（仅显示最近 500）</span>
          )}
        </h2>
        {attempts.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            {converted ? '已归到注册用户名下，请在用户详情页查看。' : '还没有答题记录。'}
          </p>
        ) : (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden backdrop-blur">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">时间</th>
                  <th className="text-left px-4 py-2.5">维度</th>
                  <th className="text-left px-4 py-2.5">题目</th>
                  <th className="text-left px-4 py-2.5">选项</th>
                  <th className="text-left px-4 py-2.5">对错</th>
                  <th className="text-right px-4 py-2.5">得分</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((att) => (
                  <AttemptRow key={att.id} att={att} fmtDateTime={fmtDateTime} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</p>
      <p className="text-slate-200">{children}</p>
    </div>
  );
}

// 窄列用缩写 (完整标签见 shared DIMENSION_LABELS: 基础认知/翻牌前/翻牌/转河复杂处理).
// 缩写词根与完整版对齐, 仅长度精简适配访客详情的窄列.
const DIM_LABEL: Record<string, string> = {
  basics: '基础',
  preflop: '翻前',
  flop: '翻牌',
  turnRiver: '转河',
};

function AssessmentCard({
  a,
  fmtDateTime,
}: {
  a: VisitorAssessment;
  fmtDateTime: (s: string | null | undefined) => string;
}): JSX.Element {
  const scores = parseDimensionScores(a.dimension_scores);
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="font-display font-bold text-3xl text-shark-300">
              {Math.round(a.composite_score)}
            </span>
            <span className="text-slate-500 text-sm">分</span>
            <span className="px-2 py-0.5 rounded-full bg-shark-900/40 border border-shark-700/50 text-shark-200 font-semibold text-sm">
              段位 {a.rank_tier}
            </span>
          </div>
          <p className="text-xs text-slate-500">
            完成于 {fmtDateTime(a.completed_at)}
            <span className="mx-2 text-slate-700">·</span>
            开始于 {fmtDateTime(a.started_at)}
          </p>
        </div>
        {scores !== null && (
          <div className="shrink-0">
            <RadarChart values={scores} size={180} showLabels />
          </div>
        )}
      </div>
      {scores !== null && (
        <div className="grid grid-cols-4 gap-2">
          {(Object.keys(scores) as RadarDimension[]).map((d) => (
            <div
              key={d}
              className="rounded-md border border-white/10 bg-white/[0.03] px-3 py-2"
            >
              <p className="text-[10px] uppercase tracking-widest text-slate-500">
                {DIM_LABEL[d] ?? d}
              </p>
              <p className="text-lg font-semibold text-white">{Math.round(scores[d] ?? 0)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AttemptRow({
  att,
  fmtDateTime,
}: {
  att: VisitorAttempt;
  fmtDateTime: (s: string | null | undefined) => string;
}): JSX.Element {
  return (
    <tr className="border-t border-white/[0.08] hover:bg-white/[0.03]">
      <td className="px-4 py-2 text-slate-400 whitespace-nowrap">
        {fmtDateTime(att.attempted_at)}
      </td>
      <td className="px-4 py-2">
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-white/[0.03] border border-white/10 text-slate-300">
          {DIM_LABEL[att.dimension] ?? att.dimension}
        </span>
      </td>
      <td className="px-4 py-2 text-slate-300 font-mono text-xs">{att.question_id}</td>
      <td className="px-4 py-2 text-slate-400 font-mono text-xs">
        {att.picked_option_id ?? '—'}
      </td>
      <td className="px-4 py-2">
        {att.is_correct === 1 ? (
          <span className="text-emerald-400">✓</span>
        ) : (
          <span className="text-rose-400">✗</span>
        )}
      </td>
      <td className="px-4 py-2 text-right text-slate-300">{Math.round(att.score * 100)}</td>
    </tr>
  );
}

/**
 * Defensive JSON.parse for the `dimension_scores` column. Returns null if
 * the row's payload is malformed — caller hides the chart in that case
 * rather than crashing the whole page.
 */
function parseDimensionScores(raw: string): Record<RadarDimension, number> | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, number>;
    if (
      typeof parsed.basics === 'number' &&
      typeof parsed.preflop === 'number' &&
      typeof parsed.flop === 'number' &&
      typeof parsed.turnRiver === 'number'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}
